/**
 * BulkActionBar - Floating action bar for bulk operations
 */
import { useState } from 'react'
import { Button, Modal } from './common'
import type { Tag, Portfolio } from '../types'
import type { BulkTagAction } from '../api/holdings'

interface BulkActionBarProps {
  selectedCount: number
  onClearSelection: () => void
  // Holdings-specific
  portfolios?: Portfolio[]
  onBulkPortfolio?: (portfolioId: string) => Promise<void>
  // Shared
  tags?: Tag[]
  onBulkTags?: (action: BulkTagAction, tagIds: string[]) => Promise<void>
  onBulkDelete?: () => Promise<void>
  isLoading?: boolean
}

export function BulkActionBar({
  selectedCount,
  onClearSelection,
  portfolios,
  onBulkPortfolio,
  tags,
  onBulkTags,
  onBulkDelete,
  isLoading = false,
}: BulkActionBarProps) {
  const [showTagModal, setShowTagModal] = useState(false)
  const [showPortfolioModal, setShowPortfolioModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [tagAction, setTagAction] = useState<BulkTagAction>('add')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('')

  const handleTagSubmit = async () => {
    if (onBulkTags && selectedTagIds.length > 0) {
      try {
        await onBulkTags(tagAction, selectedTagIds)
        onClearSelection()
      } catch (error) {
        console.error('Bulk tag operation failed:', error)
        alert('Failed to update tags. Please try again.')
      } finally {
        setShowTagModal(false)
        setSelectedTagIds([])
      }
    }
  }

  const handlePortfolioSubmit = async () => {
    if (onBulkPortfolio && selectedPortfolioId) {
      try {
        await onBulkPortfolio(selectedPortfolioId)
        onClearSelection()
      } catch (error) {
        console.error('Bulk portfolio operation failed:', error)
        alert('Failed to update portfolio. Please try again.')
      } finally {
        setShowPortfolioModal(false)
        setSelectedPortfolioId('')
      }
    }
  }

  const handleDeleteSubmit = async () => {
    if (onBulkDelete) {
      try {
        await onBulkDelete()
        onClearSelection()
      } catch (error) {
        console.error('Bulk delete operation failed:', error)
        alert('Failed to delete items. Please try again.')
      } finally {
        setShowDeleteModal(false)
      }
    }
  }

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  if (selectedCount === 0) return null

  return (
    <>
      {/* Floating action bar */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-gray-900 text-white rounded-lg shadow-lg px-6 py-3 flex items-center space-x-4">
          <span className="font-medium">
            {selectedCount} selected
          </span>
          <div className="h-6 w-px bg-gray-600" />

          {portfolios && onBulkPortfolio && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowPortfolioModal(true)}
              disabled={isLoading}
            >
              Set Portfolio
            </Button>
          )}

          {tags && onBulkTags && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowTagModal(true)}
              disabled={isLoading}
            >
              Manage Tags
            </Button>
          )}

          {onBulkDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={isLoading}
            >
              Delete
            </Button>
          )}

          <div className="h-6 w-px bg-gray-600" />
          <button
            className="text-gray-400 hover:text-white transition-colors"
            onClick={onClearSelection}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Tag Modal */}
      <Modal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        title={`Manage Tags for ${selectedCount} items`}
        size="md"
      >
        <div className="space-y-4">
          {/* Action radio buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action
            </label>
            <div className="flex space-x-4">
              {(['add', 'remove', 'set'] as BulkTagAction[]).map((action) => (
                <label key={action} className="flex items-center">
                  <input
                    type="radio"
                    name="tagAction"
                    value={action}
                    checked={tagAction === action}
                    onChange={(e) => setTagAction(e.target.value as BulkTagAction)}
                    className="mr-2"
                  />
                  <span className="text-sm capitalize">{action}</span>
                  <span className="text-xs text-gray-500 ml-1">
                    {action === 'add' && '(merge)'}
                    {action === 'remove' && '(delete)'}
                    {action === 'set' && '(replace all)'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Tag selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            {tags?.length === 0 ? (
              <p className="text-sm text-gray-500">No tags available.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags?.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.tag_id)
                  return (
                    <button
                      key={tag.tag_id}
                      type="button"
                      onClick={() => toggleTag(tag.tag_id)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{
                        backgroundColor: `${tag.color || '#6B7280'}20`,
                        color: tag.color || '#6B7280',
                        borderColor: tag.color || '#6B7280',
                      }}
                    >
                      {tag.name}
                      {isSelected && (
                        <svg className="ml-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowTagModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTagSubmit}
              disabled={selectedTagIds.length === 0 || isLoading}
              isLoading={isLoading}
            >
              Apply to {selectedCount} items
            </Button>
          </div>
        </div>
      </Modal>

      {/* Portfolio Modal */}
      {portfolios && (
        <Modal
          isOpen={showPortfolioModal}
          onClose={() => setShowPortfolioModal(false)}
          title={`Move ${selectedCount} holdings to portfolio`}
          size="sm"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portfolio
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={selectedPortfolioId}
                onChange={(e) => setSelectedPortfolioId(e.target.value)}
              >
                <option value="">Select portfolio...</option>
                {portfolios.map((portfolio) => (
                  <option key={portfolio.portfolio_id} value={portfolio.portfolio_id}>
                    {portfolio.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowPortfolioModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePortfolioSubmit}
                disabled={!selectedPortfolioId || isLoading}
                isLoading={isLoading}
              >
                Move {selectedCount} holdings
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete {selectedCount} item{selectedCount !== 1 ? 's' : ''}?
            This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteSubmit}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Delete {selectedCount} item{selectedCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
